import { copySource } from "./copySource";
import { leafLabel, kindWord } from "./kindWords";
import {
  REASON_AGREED,
  REASON_ANSWERED_BEFORE,
  LEAF_NEW,
  REASON_PRE_FILLED,
  REASON_REPEATED_CONCEPT,
  REASON_UNIT_MISSING,
} from "./reviewCodes";

export type SentenceRow = {
  leaf?: unknown;
  path?: unknown;
  kind?: unknown;
  leafSource?: unknown;
  reviewReason?: unknown;
  reviewDetail?: unknown;
};

export type DecisionTone = "good" | "warn" | "none";
export type PickedField = { path: string | null; label: string };

const sentence = (...parts: string[]) => parts.filter(Boolean).join(" ");

const copiedSentence = (detail: string) => {
  const { store, field } = copySource(detail);
  if (!store)
    return field
      ? `Copied from an older answer for a field named ${field.split("|")[0]}.`
      : "Copied from an older answer.";
  return field
    ? `Copied from ${store} field ${field}.`
    : `Copied from ${store}.`;
};

export function decisionSentence(
  row: SentenceRow,
  pickedLeaf: string | null,
  needLook: number,
  sameLeafLabel?: string,
  lead?: string | null,
  lostWords = "",
  pick?: PickedField,
): { text: string; tone: DecisionTone } {
  const pathChanged = Boolean(pick?.path) && pick?.path !== row.path;
  const hasPick = Boolean(pickedLeaf) || pathChanged;
  const reason = row.reviewReason;
  const detail = String(row.reviewDetail ?? "").trim();
  const look = needLook
    ? `${needLook} store value${needLook === 1 ? " needs" : "s need"} a look.`
    : "";
  const share =
    hasPick && sameLeafLabel ? `${sameLeafLabel} also uses this field.` : "";
  const done = (text: string, tone: DecisionTone) => ({
    text: [text, look, lostWords, share].filter(Boolean).join(" "),
    tone: needLook && tone === "good" ? "warn" : tone,
  });
  if (reason === REASON_UNIT_MISSING)
    return done("This field has a unit, but the kind holds none.", "warn");
  if (!hasPick) {
    if (reason === REASON_REPEATED_CONCEPT)
      return done(
        "No pick yet. Another field on this form means the same thing.",
        "warn",
      );
    return done("No pick yet.", "none");
  }
  const label = pick?.label ?? leafLabel(pickedLeaf ?? "");
  if (pathChanged || pickedLeaf !== row.leaf)
    return done(`You picked ${label}.`, "good");
  if (lead) return done(lead, "warn");
  if (reason === REASON_PRE_FILLED) return done(copiedSentence(detail), "good");
  if (reason === REASON_ANSWERED_BEFORE)
    return done("This store answered this field before.", "good");
  if (row.leafSource === LEAF_NEW)
    return done(
      sentence(`AI wants a new field, ${label}.`, "Approving creates it."),
      "good",
    );
  const kind = row.kind ? kindWord(String(row.kind)) : "";
  const agreed =
    reason === REASON_AGREED && kind ? `Both AIs say the kind is ${kind}.` : "";
  return done(sentence(`AI picks ${label}.`, agreed), "good");
}
